import queryString from 'query-string'
import { useMemo, useState } from 'react'
import { Box } from 'shared/components/Box/Box'
import { InlineSpinner } from 'shared/components/Spinner'
import { Text } from 'shared/components/Text/Text'
import { Colors } from 'shared/theme/colors'
import styled from 'styled-components'

import { isConsole } from '../../config/config'
import { useOnboardingChecklist } from '../../hooks/query/useOnboardingChecklist'
import {
  OnboardingChecklist,
  OnboardingChecklistName,
  shouldShowOnboardingChecklist,
} from '../../models/onboarding'
import { Team } from '../../models/team'
import { User } from '../../models/user'
import { FlexBox } from '../Base/FlexBox'
import { Task, TaskList, TaskStatusEnum } from '../TaskList/TaskList'

interface OnboardingTaskListProps {
  user: User
  team: Team
  isCurrentUser?: boolean
  parseTaskList: (
    checklist: readonly OnboardingChecklist[],
    user: User,
    team: Team,
    isApplicationChecklist?: boolean
  ) => ReadonlyArray<Task>
  children?: (
    tasks: ReadonlyArray<Task>,
    applicationTasks: ReadonlyArray<Task>,
    roadPreparationTasks: ReadonlyArray<Task>
  ) => React.ReactNode
  renderApplicationSummary?: () => JSX.Element
  renderWelcomeBack?: () => JSX.Element
}

export const contractorPaymentsItems = [
  OnboardingChecklistName.CONTRACTOR_PAYMENTS_PROFILE,
  OnboardingChecklistName.PAYROLL_BANK_ACCOUNT,
  OnboardingChecklistName.ADD_CONTRACTOR,
  OnboardingChecklistName.CONTRACTOR_PERSONAL_PROFILE,
  OnboardingChecklistName.CONTRACTOR_PERSONAL_DIRECT_DEPOSIT,
  OnboardingChecklistName.CONTRACTOR_PERSONAL_DEBIT_CARD,
]

const NON_REQUIRED_TASKS: OnboardingChecklistName[] = [OnboardingChecklistName.FUEL_CARD]

export function OnboardingTaskList({
  user,
  team,
  isCurrentUser,
  parseTaskList,
  children,
  renderApplicationSummary,
  renderWelcomeBack,
}: OnboardingTaskListProps) {
  const userId = user.id
  const isCreditOnly = team.isCreditOnly
  const queryParams = queryString.parse(window.location.search)
  const skipWelcomeBack = !!queryParams.skip_welcome_back
  const skipApplicationSummary = !!queryParams.skip_application_summary
  const forceShowOnboardingTasks = Boolean(queryString.parse(window.location.search).onboarding)
  const [showAllItems, setShowAllItems] = useState(Boolean(queryString.parse(window.location.search).all))
  const { checklist, roadChecklist, allChecklistItems, onboardingStatus, isLoading } = useOnboardingChecklist(
    userId,
    {
      refetchOnMount: true,
      refetchInterval: 5000,
    }
  )
  const filteredChecklist = useMemo(() => {
    return checklist?.filter((c) => !contractorPaymentsItems.includes(c.name) || showAllItems)
  }, [checklist, showAllItems])

  const applicationTasks = useMemo(() => {
    if (user && filteredChecklist) {
      return parseTaskList(filteredChecklist, user, team, true)
    }
    return []
  }, [filteredChecklist, user, team, parseTaskList])

  const roadPreparationTasks = useMemo(() => {
    if (user && roadChecklist) {
      return parseTaskList(roadChecklist, user, team, false)
    }
    return []
  }, [roadChecklist, user, team, parseTaskList])

  if (isLoading) {
    return <InlineSpinner />
  }
  const showRoadChecklist =
    shouldShowOnboardingChecklist(filteredChecklist, roadChecklist) || forceShowOnboardingTasks

  function getTasks() {
    if (isConsole()) return applicationTasks
    if (showRoadChecklist) return roadPreparationTasks
    return applicationTasks
  }

  const tasks = getTasks()

  function renderHeader() {
    if (isConsole()) {
      return (
        <FlexBox justifyContent="space-between" alignItems="center" mb={-2}>
          <Text variant="header3" align="left">
            Application tasks
          </Text>
        </FlexBox>
      )
    }

    if (!isCurrentUser) {
      return (
        <FlexBox isColumn justifyContent="center" alignItems="center" mb={8}>
          <Text variant="header1" color={Colors.primaryBlack}>
            Your {user.isTeamDriver ? 'driver' : ''} {user.isTeamAdmin ? 'admin' : ''}
          </Text>

          <Text variant="label2" color={Colors.darkGray}>
            {user.firstName} {user.lastName}
          </Text>
        </FlexBox>
      )
    }

    if (onboardingStatus?.header) {
      return (
        <FlexBox isColumn justifyContent="center" alignItems="center" mb={8}>
          <Text variant="header1" color={Colors.primaryBlack}>
            {onboardingStatus?.header}
          </Text>

          {onboardingStatus?.subheader && (
            <FlexBox mt={1}>
              <Text variant="label2" color={Colors.darkGray}>
                {onboardingStatus?.subheader}
              </Text>
            </FlexBox>
          )}

          <Box mt={5}></Box>
        </FlexBox>
      )
    }

    if (isCreditOnly) {
      return (
        <Text variant="header1" as="h1" styles="margin-top: 0;">
          {isCurrentUser ? 'Your' : `${user.firstName || 'Their'}'s`}{' '}
          {isCreditOnly ? 'credit application is in progress' : 'onboarding is in progress'}.
        </Text>
      )
    }

    if (showRoadChecklist) {
      if (!user?.isTeamAdmin) {
        return (
          <Text variant="header1" as="h1" styles="margin-top: 0;">
            Complete {isCurrentUser ? 'your' : ''} onboarding tasks
          </Text>
        )
      }

      return (
        <Text variant="header1" as="h1" styles="margin-top: 0;">
          Finish setting up your fleet
        </Text>
      )
    }

    return (
      <Text variant="header1" as="h1" styles="margin-top: 0;">
        Apply to drive with CloudTrucks
      </Text>
    )
  }

  function renderRoadPreparationInConsole() {
    if (!isConsole() || !roadPreparationTasks?.length) return null
    return (
      <>
        <Text variant="header3" mt={4}>
          Onboarding tasks
        </Text>
        <TaskList tasks={roadPreparationTasks} isReadOnly={false} user={user} />
      </>
    )
  }

  if (
    !skipApplicationSummary &&
    user.isTeamDriver &&
    checklist[0]?.status === TaskStatusEnum.NOT_STARTED &&
    renderApplicationSummary &&
    isCurrentUser
  ) {
    return renderApplicationSummary()
  }

  if (
    !skipWelcomeBack &&
    checklist[0]?.status !== TaskStatusEnum.NOT_STARTED &&
    allChecklistItems
      .filter((c) => !NON_REQUIRED_TASKS.includes(c.name))
      .some((c) => [TaskStatusEnum.NOT_STARTED, TaskStatusEnum.ERROR].includes(c.status)) &&
    renderWelcomeBack &&
    isCurrentUser
  ) {
    return renderWelcomeBack()
  }

  return (
    <>
      <Center>
        {renderHeader()}
        <Box m={isConsole() ? 4 : 5} />
      </Center>

      <TaskList tasks={tasks} isReadOnly={!isCurrentUser && !isConsole()} user={user} />
      {renderRoadPreparationInConsole()}

      {children?.(tasks, applicationTasks, roadPreparationTasks)}
      {isConsole() && checklist?.length !== filteredChecklist?.length ? (
        <Text variant="paragraph3" onClick={() => setShowAllItems(!showAllItems)} align="left" mt={2}>
          {showAllItems ? 'Hide' : 'Show'} all items
        </Text>
      ) : null}
    </>
  )
}

const Center = styled.div`
  text-align: center;
`
